import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { volApi, storeVolSession, formatVolError, isVolLoggedIn } from "./volApi";
import { HARBORWELL_LOGO } from "@/lib/api";
import { Loader2, HandHeart } from "lucide-react";

export default function VolunteerLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (isVolLoggedIn()) return <Navigate to="/volunteer" replace />;

  const expired = typeof window !== "undefined" && window.location.search.includes("expired=1");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await volApi.post("/volunteers/login", { email, password });
      storeVolSession(data.access_token, data.volunteer);
      navigate("/volunteer");
    } catch (err) {
      setError(formatVolError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#1E3A8A] text-white">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/95 p-1.5 flex items-center justify-center">
            <img src={HARBORWELL_LOGO} alt="HarborWell" className="h-full" />
          </div>
          <div>
            <div className="font-display text-xl font-bold">HarborWell</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#E8ECE7]">Volunteer Portal</div>
          </div>
        </div>
        <div className="max-w-md">
          <div className="hw-eyebrow text-[#F59E0B] mb-3">A guiding light</div>
          <h1 className="font-display text-4xl font-semibold leading-tight mb-4">
            Walk alongside families across Eastern North Carolina.
          </h1>
          <p className="text-white/85 leading-relaxed">
            HarborWell volunteers help with outreach events, kit distributions,
            family visits, and more. Sign in to RSVP to upcoming events and log your hours.
          </p>
        </div>
        <div className="text-xs text-white/70 flex items-center gap-2">
          <HandHeart className="w-3.5 h-3.5" strokeWidth={1.75} />
          A 501(c)(3) nonprofit · Eastern North Carolina
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex lg:hidden items-center justify-center mb-8">
            <img src={HARBORWELL_LOGO} alt="HarborWell" className="h-16" />
          </div>
          <div className="hw-eyebrow mb-2">Volunteer sign-in</div>
          <h2 className="font-display text-3xl font-semibold text-[#0F172A] mb-2">Welcome back</h2>
          <p className="text-[#334155] mb-8">Sign in to your volunteer account.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="volunteer-login-form">
            {expired && (
              <div className="px-4 py-3 rounded-xl bg-[#FEF3C7] border border-[#FCD34D] text-sm text-[#92400E]">
                Your session expired. Please sign in again.
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="volunteer-login-email"
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Password</label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="volunteer-login-password"
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid="volunteer-login-error" className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="volunteer-login-submit"
              className="w-full py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#E5E5E0] text-sm text-[#334155]">
            New to HarborWell?{" "}
            <Link to="/volunteer/signup" data-testid="volunteer-go-signup" className="text-[#1E3A8A] font-medium hover:underline">
              Sign up to volunteer
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
